/**
 * パフォーマンスダッシュボード (SCR-P-001) の定数
 *
 * 色は VI 定義 §7.4・SCR-P-001 §3.3・§4.2 の値。CSS 変数は足さない決まり（VI 定義 §3.3「CSS 変数の決まり」）のため、
 * recharts に渡す色はここに同じ HEX で持つ。
 */

/** 期間の単位（CD-005。並び順もコード定義のとおり） */
export const PERIODS = {
    WEEKLY: 'weekly',
    MONTHLY: 'monthly',
    QUARTERLY: 'quarterly',
};

export const PERIOD_OPTIONS = [
    { value: PERIODS.WEEKLY, label: '週次' },
    { value: PERIODS.MONTHLY, label: '月次' },
    { value: PERIODS.QUARTERLY, label: '四半期' },
];

/** 初期表示の条件（SCR-P-001 §6.1。選択は再読み込みで保たない: #1958 の決定 D1） */
export const DEFAULT_PERIOD = PERIODS.MONTHLY;

/** 途中の期間の注記に使う「今の期間」の呼び方（SCR-P-001 C10。#1958 の決定 D8） */
export const CURRENT_PERIOD_NAMES = {
    [PERIODS.WEEKLY]: '今週',
    [PERIODS.MONTHLY]: '今月',
    [PERIODS.QUARTERLY]: '今四半期',
};

/** 担当者フィルタで選べる人数の上限（SCR-P-001 IN-P-002・§5.1 #2。集計 API 設計 §2.1） */
export const MAX_ASSIGNEES = 50;

/** 見せる範囲の表示（DP-P-010。集計 API 設計 §2.5 の scope.kind） */
export const SCOPE_LABELS = {
    self: '自分の実績',
    organization: '全員',
    assignees: '選んだ担当者',
};

/** グラフのカードの見出し（DP-P-005〜008。並びは画面の並び。読み込み中のスケルトンも同じ見出しを出す） */
export const CHART_TITLES = {
    decisionRate: '決定率トレンド',
    assigneeActivity: '担当者別活動量',
    stageTransition: '案件ステージ推移',
    speechTopic: '発話比率・トピック分布',
};

/** 画面の文言（SCR-P-001 §6.3。VI 定義 §7.3 も同じ文言を正としている） */
export const DASHBOARD_MESSAGES = {
    empty: '対象期間のデータがありません',
    noMeetings: '対象期間に面談がありません',
    decisionRateGap: '線が切れている期間は、対象期間に面談がありません',
    // 「今日までに」は、その期間に面談した方が今日までに内定承諾に入ったかで数える確定した読み（集計 API 設計 §5.4。#2178 の丹羽様のご判断）。文は SCR-P-001 §6.5 の 35
    decisionRateBasis: 'その期間に面談した方のうち、今日までに内定承諾に至った方の割合です。直近の期間ほど、後から値が上がるため、決定率の前期比は出していません。',
    forbidden: 'この集計を閲覧する権限がありません。',
    failed: 'データの取得に失敗しました。再読み込みしてください',
    reload: '再読み込み',
    pageLoadFailed: '画面の読み込みに失敗しました。ページを再読み込みしてください',
};

export const NOT_APPLICABLE = 'N/A';

/** チャートの系列色（VI 定義 §7.4「カテゴリ系列」1〜6） */
export const SERIES_COLORS = ['#0176d3', '#0D9488', '#D97706', '#7C3AED', '#E11D48', '#64748B'];

/** グラフの文字の色（Text `--color-text`。SCR-P-001 §3.3） */
export const TEXT_COLOR = '#16325c';

/** グラフの補足の文字の色（Text Secondary `--color-text-secondary`。VI 定義 §7.4） */
export const TEXT_SECONDARY_COLOR = '#556a84';

/**
 * 凡例の文字の色。recharts 3 は labelStyle.color が無いと凡例の文字を系列の色で描き、
 * 淡い系列（推薦済み #B3D6F2 等）は白地で 4.5:1 に届かない。系列の色は凡例の印だけに使う（SCR-P-001 §3.3）
 */
export const LEGEND_LABEL_STYLE = { color: TEXT_COLOR };

/**
 * 凡例・ツールチップの並べ替えを切り、系列を描いた順（積み上げの下から）のまま出す（SCR-P-001 G07 ②・G08）。
 * recharts 3.10.1 は既定で凡例を value（系列の名前）、ツールチップを name の文字の順に並べ替える
 * （component/Legend.js・component/Tooltip.js の既定値）。#1958 comment 6342728615 の F4
 */
export const KEEP_SERIES_ORDER = null;

/** 前期比の良し悪しの色（SCR-P-001 C10） */
export const CHANGE_TONES = {
    GOOD: 'good', // Success Text #2e7d32
    BAD: 'bad', // Warning Dark #B45309
    NEUTRAL: 'neutral', // Text Secondary #556a84（差 0・比べられない・平均発話比率）
};

/** 案件ステージの段階色（SCR-P-001 G07。色はステージの名前で割り当てる） */
export const STAGE_COLORS = {
    面談前: '#94A3B8',
    面談済み: '#64748B',
    推薦済み: '#B3D6F2',
    一次選考: '#73B4E7',
    二次選考: '#3491DC',
    三次面接: '#0176d3',
    最終選考: '#015394',
    内定: '#34a853',
    内定承諾: '#2e7d32',
    終了: '#334155',
};

/** マスタに無い名前の履歴をまとめた区分（集計 API 設計 §4.4）。積み上げのいちばん上に置く */
export const OTHER_STAGE_NAME = 'その他';
export const OTHER_STAGE_COLOR = '#CBD5E1';

/** 上の 10 件以外の名前のステージに順に使う色（G07: 系列 4・5） */
export const EXTRA_STAGE_COLORS = ['#7C3AED', '#E11D48'];

/** 発話比率の 3 色と呼び方（G08。活動詳細の話者分離と同じ） */
export const SPEAKERS = [
    { key: 'agent', label: 'エージェント', color: '#0176d3' },
    { key: 'candidate', label: '求職者', color: '#0D9488' },
    { key: 'unknown', label: '不明', color: '#6b7c93' },
];

/** トピックの棒の色（G08: 灰の単色） */
export const TOPIC_COLOR = '#64748B';

/** 発話比率のいちばん上の「全体」の行（G08。値は speech-topic の overall） */
export const OVERALL_SPEECH_LABEL = '全体';
